import { useSyncExternalStore } from 'react';

/**
 * Tiny store linking the bottom bar (owns the empty slot node beside the nav
 * pill) and the AI button (portals into it). No React context: both live far
 * apart in the tree and the slot is a DOM node, not state.
 */
let slotNode = null;
let sidebarNode = null;
let occupied = false;
let snapshot = { slot: null, sidebarSlot: null, occupied: false };
const listeners = new Set();

function emit() {
  snapshot = { slot: slotNode, sidebarSlot: sidebarNode, occupied };
  listeners.forEach((l) => l());
}

/**
 * Ref callback (React 19): attaching sets the slot and RETURNS a cleanup that clears it only if
 * this node is still the current one — a late unmount of a kept-alive copy can't null a live slot.
 * Called with null (tests / manual reset) it clears unconditionally.
 */
export function setAiDockSlot(node) {
  const next = node || null;
  if (next !== slotNode) { slotNode = next; emit(); }
  if (!next) return undefined;
  return () => { if (slotNode === next) { slotNode = null; emit(); } };
}

/** The desktop sidebar's slot (KAN-1147) — separate from the bar's so the two shells never fight over one node. Same guarded cleanup. */
export function setAiSidebarSlot(node) {
  const next = node || null;
  if (next !== sidebarNode) { sidebarNode = next; emit(); }
  if (!next) return undefined;
  return () => { if (sidebarNode === next) { sidebarNode = null; emit(); } };
}

export function setAiDockOccupied(value) {
  const next = Boolean(value);
  if (next === occupied) return;
  occupied = next;
  emit();
}

function subscribe(listener) {
  listeners.add(listener);
  return () => listeners.delete(listener);
}

const getSnapshot = () => snapshot;

/** { slot, sidebarSlot, occupied } — the bottom bar's and the desktop sidebar's AI slot nodes (or null) and whether the AI button sits in one. */
export function useAiDockSlot() {
  return useSyncExternalStore(subscribe, getSnapshot, getSnapshot);
}
